import React, { useEffect, useState } from "react";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
  ListSubheader,
  Tooltip,
  Menu,
  IconButton,
} from "@mui/material";
import moment from "moment";
import SearchBar from "../../Shared/Widgets/SearchBar";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import CompanyDetail from "./CompanyDetail";

export default function Companies() {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [filter, setFilter] = useState("All");
  const [sortBy, setSortBy] = useState("A-Z");
  const [allCompanyData, setAllCompanyData] = useState<any[]>([]);
  const [selectedCompanyData, setSelectedCompanyData] = useState<any>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  // Download / Export states
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchValue]);

  const handleFilterChange = (event: any) => {
    setFilter(event.target.value);
  };

  const handleSortChange = (event: any) => {
    setSortBy(event.target.value);
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const fetchCompaniesData = async (isFirstPage = false) => {
    setLoading(true);
    try {
      const activePage = isFirstPage ? 1 : page;
      const response = await axios.get(
        `${ipconfig}/companies/fetch?search=${encodeURIComponent(debouncedSearchValue)}&filter=${filter}&sortBy=${sortBy}&page=${activePage}&limit=15`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        const companies = response?.data?.data || [];
        const pagination = response?.data?.pagination || { totalItems: companies.length, totalPages: 1, currentPage: 1 };

        if (activePage === 1) {
          setAllCompanyData(companies);
          setSelectedCompanyData(companies[0] || null);
        } else {
          setAllCompanyData((prev) => {
            const existingIds = new Set(prev.map((c: any) => c._id));
            const filteredNew = companies.filter((c: any) => !existingIds.has(c._id));
            return [...prev, ...filteredNew];
          });
        }
        setTotalPages(pagination.totalPages);
        setTotalItems(pagination.totalItems);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  };

  // Reset pagination when search, filter, or sort changes
  useEffect(() => {
    setPage(1);
    fetchCompaniesData(true);
  }, [filter, sortBy, debouncedSearchValue]);

  // Fetch subsequent pages
  useEffect(() => {
    if (page > 1) {
      fetchCompaniesData(false);
    }
  }, [page]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 30) {
      if (!loading && page < totalPages) {
        setPage((prev) => prev + 1);
      }
    }
  };

  const handleExport = async (format: "excel" | "pdf") => {
    handleDownloadClose();
    setExportLoading(true);
    try {
      const response = await axios.get(
        `${ipconfig}/companies/fetch?search=${encodeURIComponent(debouncedSearchValue)}&filter=${filter}&sortBy=${sortBy}&page=1&limit=999999`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const companiesToExport = response?.data?.data || [];

      if (format === "excel") {
        const html = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="utf-8">
            <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook>
                <x:ExcelWorksheets>
                  <x:ExcelWorksheet>
                    <x:Name>Companies List</x:Name>
                    <x:WorksheetOptions>
                      <x:DisplayGridlines/>
                    </x:WorksheetOptions>
                  </x:ExcelWorksheet>
                </x:ExcelWorksheets>
              </x:ExcelWorkbook>
            </xml>
            <![endif]-->
          </head>
          <body>
            <table border="1">
              <tr style="background-color: #4f46e5; color: #ffffff; font-weight: bold; height: 30px;">
                <th>Company Name</th>
                <th>Synced By Email</th>
                <th>Last Synced Time</th>
                <th>Users Count</th>
                <th>Devices Count</th>
                <th>License Number</th>
              </tr>
              ${companiesToExport.map((c: any) => `
                <tr style="height: 24px;">
                  <td>${c?.tally_companyInfo?.Name || "N/A"}</td>
                  <td>${c?.syncedByEmail || "N/A"}</td>
                  <td>${c?.lastSyncedTime ? moment(c.lastSyncedTime).format("DD-MM-YYYY hh:mm A") : "Never Synced"}</td>
                  <td>${c?.totalUsers || 0}</td>
                  <td>${c?.totalDevices || 0}</td>
                  <td>${c?.tally_companyInfo?.licenseNumber || "N/A"}</td>
                </tr>
              `).join("")}
            </table>
          </body>
          </html>
        `;

        const blob = new Blob([html], { type: "application/vnd.ms-excel" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Companies_Report_${moment().format("YYYYMMDD_HHmmss")}.xls`;
        link.click();
        URL.revokeObjectURL(url);
      } else if (format === "pdf") {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          const html = `
            <html>
            <head>
              <title>Companies Directory Report</title>
              <style>
                body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #1e293b; background-color: #ffffff; }
                .header-container { border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; margin-bottom: 24px; }
                h1 { margin: 0; color: #4f46e5; font-size: 24px; font-weight: 800; }
                .meta { font-size: 12px; color: #64748b; margin-top: 6px; }
                table { width: 100%; border-collapse: collapse; margin-top: 16px; }
                th, td { border: 1px solid #e2e8f0; padding: 10px 12px; text-align: left; font-size: 11px; }
                th { background-color: #f8fafc; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
                tr:nth-child(even) { background-color: #f8fafc50; }
                @media print {
                  body { padding: 0; }
                  @page { margin: 1cm; }
                }
              </style>
            </head>
            <body>
              <div class="header-container">
                <h1>Accosync Companies Directory</h1>
                <div class="meta">
                  Generated: ${moment().format("DD MMM YYYY, hh:mm A")} | Filter: <span style="text-transform: capitalize; font-weight: 600;">${filter}</span> | Sorted: <span style="text-transform: capitalize; font-weight: 600;">${sortBy}</span> | Total Records: ${companiesToExport.length}
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Synced By Email</th>
                    <th>Last Synced</th>
                    <th>Users</th>
                    <th>Devices</th>
                    <th>Tally License</th>
                  </tr>
                </thead>
                <tbody>
                  ${companiesToExport.map((c: any) => `
                    <tr>
                      <td style="font-weight: 600; color: #0f172a;">${c?.tally_companyInfo?.Name || "N/A"}</td>
                      <td>${c?.syncedByEmail || "N/A"}</td>
                      <td>${c?.lastSyncedTime ? moment(c.lastSyncedTime).format("DD MMM YYYY, hh:mm A") : "Never Synced"}</td>
                      <td>${c?.totalUsers || 0}</td>
                      <td>${c?.totalDevices || 0}</td>
                      <td>${c?.tally_companyInfo?.licenseNumber || "N/A"}</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
              <script>
                window.onload = function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 500);
                };
              </script>
            </body>
            </html>
          `;
          printWindow.document.write(html);
          printWindow.document.close();
        }
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", gap: "10px", p: 1 }}>
      {/* Left Side: Search Bar + Company List */}
      <Box sx={{ width: "320px", display: "flex", flexDirection: "column", gap: "12px", flexShrink: 0 }}>
        <Box sx={{ width: "100%" }}>
          <SearchBar onSearch={setSearchValue} />
        </Box>

        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "12px 16px",
              borderBottom: "1px solid",
              borderColor: "divider",
              gap: "8px"
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Typography sx={{ color: "text.primary", fontSize: "14px", fontWeight: 700, whiteSpace: "nowrap" }}>
                Companies ({totalItems})
              </Typography>
              <Tooltip title="Download Report">
                <IconButton
                  size="small"
                  onClick={handleDownloadClick}
                  sx={{ color: "primary.main", p: 0.5 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-download">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", gap: "8px", width: "100%" }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="company-filter-label" sx={{ fontSize: "12.5px" }}>Filter</InputLabel>
                <Select
                  labelId="company-filter-label"
                  value={filter}
                  label="Filter"
                  onChange={handleFilterChange}
                  sx={{ fontSize: "12px" }}
                >
                  <MenuItem value="All" sx={{ fontSize: "12px" }}>All Status</MenuItem>
                  <MenuItem value="active" sx={{ fontSize: "12px" }}>Active last 7 days</MenuItem>
                  <MenuItem value="inactive" sx={{ fontSize: "12px" }}>{"Inactive > 7 days"}</MenuItem>
                  <MenuItem value="never-synced" sx={{ fontSize: "12px" }}>Never Synced</MenuItem>
                  <MenuItem value="tally-connected" sx={{ fontSize: "12px" }}>Tally Connected</MenuItem>
                  <MenuItem value="non-tally" sx={{ fontSize: "12px" }}>Non-Tally</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="company-sort-label" sx={{ fontSize: "12.5px" }}>Sort By</InputLabel>
                <Select
                  labelId="company-sort-label"
                  value={sortBy}
                  label="Sort By"
                  onChange={handleSortChange}
                  sx={{ fontSize: "12px" }}
                >
                  <MenuItem value="A-Z" sx={{ fontSize: "12px" }}>A-Z Name</MenuItem>
                  <MenuItem value="Z-A" sx={{ fontSize: "12px" }}>Z-A Name</MenuItem>
                  <MenuItem value="synced-latest" sx={{ fontSize: "12px" }}>Synced New to Old</MenuItem>
                  <MenuItem value="synced-oldest" sx={{ fontSize: "12px" }}>Synced Old to New</MenuItem>
                  <MenuItem value="users-desc" sx={{ fontSize: "12px" }}>Users High to Low</MenuItem>
                  <MenuItem value="users-asc" sx={{ fontSize: "12px" }}>Users Low to High</MenuItem>
                  <MenuItem value="devices-desc" sx={{ fontSize: "12px" }}>Devices High to Low</MenuItem>
                  <MenuItem value="devices-asc" sx={{ fontSize: "12px" }}>Devices Low to High</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {exportLoading || (loading && page === 1) ? (
            <Spinner height="100%" />
          ) : (
            <>
              <Box 
                id="company-list-container"
                onScroll={handleScroll}
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  padding: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  "&::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "divider",
                    borderRadius: "4px",
                  },
                }}
              >
                {allCompanyData.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>No companies found</Typography>
                  </Box>
                ) : (
                  allCompanyData.map((item: any, index: any) => {
                    const isSelected = selectedCompanyData && selectedCompanyData._id === item._id;
                    return (
                      <Box
                        key={item._id || index}
                        onClick={() => setSelectedCompanyData(item)}
                        sx={{
                          padding: "12px 16px",
                          cursor: "pointer",
                          backgroundColor: isSelected
                            ? theme.palette.mode === "light" ? "#f0f7ff" : "#1e3a8a33"
                            : "background.paper",
                          border: "1px solid",
                          borderColor: isSelected ? "primary.main" : "divider",
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b80",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item?.tally_companyInfo?.Name || "Unnamed Company"}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, mr: 1 }}>
                            {item?.dbName ? `DB: ${item.dbName}` : ""}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "11px", flexShrink: 0 }}>
                            Users: {item?.totalUsers || 0} | Devices: {item?.totalDevices || 0}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              {loading && page > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                  <Spinner height="30px" />
                </Box>
              )}
            </>
          )}
        </Box>

        <Menu
          anchorEl={downloadAnchorEl}
          open={Boolean(downloadAnchorEl)}
          onClose={handleDownloadClose}
          PaperProps={{
            sx: { minWidth: "140px", borderRadius: "8px", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)" }
          }}
        >
          <MenuItem onClick={() => handleExport("excel")} sx={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sheet">
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
              <line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="3" y1="15" x2="21" y2="15"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
              <line x1="15" y1="3" x2="15" y2="21"/>
            </svg>
            Excel Report
          </MenuItem>
          <MenuItem onClick={() => handleExport("pdf")} sx={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
              <path d="M10 9H8"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
            </svg>
            PDF Report
          </MenuItem>
        </Menu>
      </Box>

      {/* Right Side: Details */}
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: "background.paper",
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "auto",
          padding: "20px",
          height: "100%",
        }}
      >
        {selectedCompanyData ? (
          <CompanyDetail
            data={selectedCompanyData}
            updatedetails={() => fetchCompaniesData(true)}
          />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 500 }}>
              No company selected.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
